import { getStorage } from '@/utils/localStorage';

export default HTTPClient => ({
  readOne: async (id) => {
    const resp = await HTTPClient.get(`questions/${id}?includeHasStudentAnswer=true&includeHasUserSolution=true`);
    return resp;
  },
  create: async ({
    alternatives, answerType, authorId, examIds, labels, statement, topicIds, unlisted,
  }) => {
    const resp = await HTTPClient.post('questions', {
      body: JSON.stringify({
        alternatives: alternatives.map(a => ({
          ...a,
          topic_ids: a.topicIds,
        })),
        answer_type: answerType,
        author_id: authorId,
        labels,
        statement,
        exam_ids: examIds,
        topic_ids: topicIds,
        unlisted,
      }),
    });
    return resp;
  },
  update: async (id, payload) => {
    const resp = await HTTPClient.put(`questions/${id}`, {
      body: JSON.stringify({
        answer_type: payload.answerType,
        labels: payload.labels,
        statement: payload.statement,
        unlisted: payload.unlisted,
      }),
    });
    return resp;
  },
  attachExam: async (questionID, examID, authorID) => {
    const resp = await HTTPClient.post(`questions/${questionID}/exam/attach`, {
      body: JSON.stringify({
        exam_id: examID,
        author_id: authorID,
      }),
    });
    return resp;
  },
  detachExam: async (questionID, examID, authorID) => {
    const resp = await HTTPClient.post(`questions/${questionID}/exam/detach`, {
      body: JSON.stringify({
        exam_id: examID,
        author_id: authorID,
      }),
    });
    return resp;
  },
  attachTopic: async (questionID, topicID, authorID, isCopy = false) => {
    const resp = await HTTPClient.post(`questions/${questionID}/topic/attach`, {
      body: JSON.stringify({
        topic_id: topicID,
        author_id: authorID,
        is_copy: isCopy,
      }),
    });
    return resp;
  },
  detachTopic: async (questionID, topicID, authorID) => {
    const resp = await HTTPClient.post(`questions/${questionID}/topic/detach`, {
      body: JSON.stringify({
        topic_id: topicID,
        author_id: authorID,
      }),
    });
    return resp;
  },
  updateDifficulty: async (id, difficultyLevel) => {
    const resp = await HTTPClient.put(`questions/${id}/difficulty`, {
      body: JSON.stringify({
        difficulty_level: difficultyLevel,
      }),
    });
    return resp;
  },
  changeStatus: async (ids, value, owner, changeActor, text, changeActorId) => {
    const resp = await HTTPClient.post('questions/status', {
      body: JSON.stringify({
        ids,
        status: {
          value,
          owner,
          text,
          change_actor: changeActor,
          change_actor_id: changeActorId,
        },
      }),
    });

    return {
      data: resp,
      errors: null,
    };
  },
  updateSolution: async (questionID, solutionID, solution) => {
    const acl = getStorage('acl', { format: true });
    const resp = await HTTPClient.put(`questions/${questionID}/solutions/${solutionID}`, {
      body: JSON.stringify({
        ...solution,
        author_id: acl.id,
        author_email: acl.email,
      }),
    });
    return resp;
  },

  updateQuestionVideo: async (questionID, video) => {
    const resp = await HTTPClient.put(`questions/${questionID}/video_solution`, {
      body: JSON.stringify({
        video_solution_id: video,
      }),
    });
    return resp;
  },

  deleteQuestionVideo: async (questionID, videoID) => {
    const resp = await HTTPClient.delete(`questions/${questionID}/video_solutions/${videoID}`);
    return resp;
  },

  createSolution: async (questionID, solution) => {
    const acl = getStorage('acl', { format: true });
    const resp = await HTTPClient.post(`questions/${questionID}/solutions`, {
      body: JSON.stringify({
        ...solution,
        author_id: acl.id,
        author_email: acl.email,
      }),
    });
    return resp;
  },
  search: async (catalogs, questionFilters, limit, offset) => {
    const filters = [];
    const addFilter = (f, entityType, value) => {
      if (!value) {
        return null;
      }

      f.push({
        entity_type: entityType,
        add: true,
        refs: value.map(v => String(v)),
      });
    };

    addFilter(filters, 'scholarity', questionFilters.scholarity);
    addFilter(filters, 'status_owner', questionFilters.status_owner);
    addFilter(filters, 'year', questionFilters.year);
    addFilter(filters, 'status', questionFilters.status);
    addFilter(filters, 'author_email', questionFilters.author_email);
    addFilter(filters, 'topic', questionFilters.topic);
    addFilter(filters, 'answer_type', questionFilters.answer_type);

    Object.keys(catalogs).forEach((k) => {
      addFilter(filters, k, catalogs[k]);
    });

    const resp = await HTTPClient.post('questions/searchbo', {
      body: JSON.stringify({
        filters,
        limit,
        offset,
        q: questionFilters.searchWord || '',
      }),
    });
    return resp;
  },
  readSolutions: async (questionID) => {
    const resp = await HTTPClient.get(`questions/${questionID}/solutions`);
    return resp?.data || [];
  },
  readVideoSolutions: async (questionID) => {
    const resp = await HTTPClient.get(`questions/${questionID}/video_solutions`);
    return resp?.data || [];
  },
  updatePartial: async (id, payload) => {
    const resp = await HTTPClient.put(`questions/${id}/partial`, {
      body: JSON.stringify({
        slug: payload.slug,
        public: payload.public,
      }),
    });
    return resp;
  },
  updateUnlistBatch: async (lines) => {
    const resp = await HTTPClient.put('questions/unlist/batch', {
      body: JSON.stringify({ lines }),
    });
    return resp;
  },
  updateHasVideoSolutionBatch: async (lines) => {
    const resp = await HTTPClient.put('questions/has_video_solution/batch', {
      body: JSON.stringify({ lines }),
    });
    return resp;
  },
  deleteQuestionCustomSolution: async (id) => {
    const resp = await HTTPClient.delete(`solutions/${id}`);
    return resp;
  },
  updateQuestionLabelsBatch: async (lines) => {
    const resp = await HTTPClient.put('questions/update_labels/batch', {
      body: JSON.stringify({ lines }),
    });
    return resp;
  },
});
